import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { tp } from "../i18n";
import { Button, Card, Input, Badge } from "../components/ui";
import ProfileForm from "../components/ProfileForm";
import { ShieldCheck, Mail, Phone, AlertTriangle, History, MonitorSmartphone, Trash2, FileText, Download, IdCard } from "lucide-react";

function TrustedDevicesCard() {
  const { lang } = useApp();
  const [devices, setDevices] = useState([]);
  const [msg, setMsg] = useState("");
  const current = localStorage.getItem("suite_device_token") || "";
  const load = () => client.get("/auth/trusted-devices", { params: { current } }).then((r) => setDevices(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const revoke = async (d) => {
    await client.delete(`/auth/trusted-devices/${d.id}`);
    if (d.current) localStorage.removeItem("suite_device_token");
    setMsg(tp("Device revoked.", lang)); load();
  };
  const revokeAll = async () => {
    if (!window.confirm(tp("Revoke all trusted devices? Every device (including this one) will require a verification code at next sign-in.", lang))) return;
    await client.post("/auth/trusted-devices/revoke-all");
    localStorage.removeItem("suite_device_token");
    setMsg(tp("All devices revoked.", lang)); load();
  };
  return (
    <Card className="space-y-3" data-testid="account-trusted-devices">
      <div className="flex items-center gap-2 font-head">
        <MonitorSmartphone size={18} className="text-[var(--primary)]" /> {tp("Trusted devices", lang)}
        {devices.length > 0 && (
          <button onClick={revokeAll} data-testid="trusted-revoke-all"
            className="ms-auto text-xs text-[#DA1E28] hover:underline">{tp("Revoke all", lang)}</button>
        )}
      </div>
      <p className="text-sm opacity-60">{tp("Devices where you chose \"Trust this device\" skip the verification code for 30 days. Revoke any you don't recognise.", lang)}</p>
      {msg && <div className="text-sm" data-testid="trusted-msg">{msg}</div>}
      <div className="rounded-md border border-black/10 dark:border-white/10" data-testid="trusted-list">
        {devices.length === 0
          ? <div className="p-3 text-sm opacity-50">{tp("No trusted devices. You'll enter a code on every sign-in.", lang)}</div>
          : devices.map((d) => (
            <div key={d.id} data-testid={`trusted-row-${d.id}`}
              className="flex items-center gap-3 border-b border-black/5 dark:border-white/5 px-3 py-2.5 text-sm last:border-0">
              <MonitorSmartphone size={16} className="opacity-60" />
              <div className="flex-1">
                <div className="font-medium">{d.label}{d.current && <Badge color="info" className="ms-2">{tp("this device", lang)}</Badge>}</div>
                <div className="text-xs opacity-50">
                  {tp("Added", lang)} {(d.created_at || "").slice(0, 10)} · {tp("expires", lang)} {(d.expires_at || "").slice(0, 10)}
                </div>
              </div>
              <button onClick={() => revoke(d)} data-testid={`trusted-revoke-${d.id}`} title={tp("Revoke", lang)}
                className="rounded-md border border-black/15 dark:border-white/15 p-1.5 text-[#DA1E28] lift"><Trash2 size={14} /></button>
            </div>
          ))}
      </div>
    </Card>
  );
}

function MonthlyLogsCard() {
  const { lang } = useApp();
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(null);   // period expanded
  const [detail, setDetail] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => { client.get("/monthly-logs").then((r) => setLogs(r.data)).catch(() => {}); }, []);

  const view = async (p) => {
    if (open === p) { setOpen(null); return; }
    setOpen(p); setDetail(null);
    try { const { data } = await client.get(`/monthly-logs/${p}`); setDetail(data); } catch (e) { setMsg(apiErr(e)); }
  };
  const downloadPdf = async (p) => {
    setBusy(p + "-pdf");
    try {
      const res = await client.get(`/monthly-logs/${p}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `monthly-log-${p}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(""); }
  };
  const emailCopy = async (p) => {
    setBusy(p + "-mail"); setMsg("");
    try { const { data } = await client.post(`/monthly-logs/${p}/email`); setMsg(`${tp("Emailed a copy to", lang)} ${data.emailed_to}.`); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(""); }
  };

  const H = ["access", "wipes", "cluster", "transactions", "payments", "alerts"];
  const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const monthLabel = (l) => {
    const m = /^(\d{4})-(\d{2})/.exec(l.period || "");
    return m ? `${tp(MONTHS[+m[2] - 1], lang)} ${m[1]}` : l.label;
  };
  return (
    <Card className="space-y-3" data-testid="account-monthly-logs">
      <div className="flex items-center gap-2 font-head"><FileText size={18} className="text-[var(--primary)]" /> {tp("Monthly logs", lang)}</div>
      <p className="text-sm opacity-60">{tp("A rolling 12-month record of activity, security and data events on your account. View or download a printable PDF any time. Need a mailed copy? Use \"Email me a copy\" or ask customer service.", lang)}</p>
      {msg && <div className="rounded-md border border-[var(--primary)]/40 px-3 py-2 text-sm" data-testid="monthly-msg">{msg}</div>}
      <div className="rounded-md border border-black/10 dark:border-white/10" data-testid="monthly-list">
        {logs.map((l) => (
          <div key={l.period} className="border-b border-black/5 dark:border-white/5 last:border-0">
            <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 text-sm">
              <div className="min-w-[130px] font-medium">{monthLabel(l)}</div>
              <div className="hidden gap-1 sm:flex">
                {H.map((k) => <Badge key={k} color={k === "wipes" && l.headline[k] ? "danger" : "neutral"}>{tp(k, lang)}: {l.headline[k] ?? 0}</Badge>)}
              </div>
              <div className="ms-auto flex items-center gap-2">
                <button onClick={() => view(l.period)} data-testid={`monthly-view-${l.period}`} className="text-xs text-[var(--primary)] hover:underline">{open === l.period ? tp("Hide", lang) : tp("View", lang)}</button>
                <button onClick={() => downloadPdf(l.period)} disabled={busy === l.period + "-pdf"} data-testid={`monthly-pdf-${l.period}`} className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-xs lift dark:border-white/15"><Download size={13} /> {tp("PDF", lang)}</button>
                <button onClick={() => emailCopy(l.period)} disabled={busy === l.period + "-mail"} data-testid={`monthly-email-${l.period}`} className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-xs lift dark:border-white/15"><Mail size={13} /> {tp("Email me a copy", lang)}</button>
              </div>
            </div>
            {open === l.period && (
              <div className="border-t border-black/5 bg-black/[0.015] px-3 py-2 text-xs dark:border-white/5 dark:bg-white/[0.02]" data-testid={`monthly-detail-${l.period}`}>
                {!detail ? <div className="opacity-50">{tp("Loading…", lang)}</div> : (
                  <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <div className="mb-1 font-semibold opacity-70">{tp("Activity & records", lang)}</div>
                      <div>{tp("Transactions", lang)}: {detail.data?.activity?.transactions} · {tp("Payments", lang)}: {detail.data?.activity?.benefit_payments} · {tp("Alerts", lang)}: {detail.data?.activity?.alerts}</div>
                      <div className="mt-1 opacity-70">{tp("Records", lang)}: {Object.entries(detail.data?.activity?.records || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}</div>
                      <div className="mt-1 opacity-70">{tp("Emails", lang)}: {detail.data?.delivery?.emails} · {tp("SMS", lang)}: {detail.data?.delivery?.sms}</div>
                    </div>
                    <div>
                      <div className="mb-1 font-semibold opacity-70">{tp("Security & data events", lang)}</div>
                      {["access", "data", "cluster", "security"].flatMap((k) => (detail.data?.security?.[k] || []).map((it, i) => (
                        <div key={k + i} className="truncate">• {(it.at || "").slice(0, 10)} {it.label || it.type}</div>
                      ))).slice(0, 12)}
                      {["access", "data", "cluster", "security"].every((k) => !(detail.data?.security?.[k] || []).length) && <div className="opacity-50">{tp("No events this month.", lang)}</div>}
                    </div>
                  </div>
                  <div className="mt-2 truncate border-t border-black/5 pt-1.5 font-mono text-[10px] opacity-50 dark:border-white/5" data-testid={`monthly-hash-${l.period}`} title={tp("Document integrity hash", lang)}>{tp("Integrity (SHA-256)", lang)}: {detail.hash}</div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Account() {
  const { user, refreshUser, lang } = useApp();
  const [recEmail, setRecEmail] = useState(user?.recovery_email || "");
  const [recMsg, setRecMsg] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [changeMsg, setChangeMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const [acct, setAcct] = useState(null);

  useEffect(() => { client.get("/auth/security-events").then((r) => setEvents(r.data)).catch(() => {}); }, []);
  useEffect(() => { client.get("/account/purchases").then((r) => setAcct(r.data)).catch(() => {}); }, []);
  const reloadAcct = () => client.get("/account/purchases").then((r) => setAcct(r.data)).catch(() => {});

  const saveRecovery = async () => {
    setRecMsg(""); setBusy(true);
    try {
      await client.post("/auth/recovery-email", { recovery_email: recEmail.trim() });
      setRecMsg(tp("Recovery email saved.", lang));
      refreshUser && refreshUser();
    } catch (e) { setRecMsg(apiErr(e)); }
    setBusy(false);
  };

  const requestChange = async () => {
    setChangeMsg(""); setBusy(true);
    try {
      const { data } = await client.post("/auth/request-contact-change", {
        new_email: newEmail.trim() || null, new_phone: newPhone.trim() || null, origin_url: window.location.origin,
      });
      setChangeMsg(`${tp("A confirmation was sent to your phone", lang)} (${data.sent_to.phone}) ${tp("and recovery email", lang)} (${data.sent_to.recovery_email}). ${tp("The change applies only after you confirm. Declining will block this account until the vendor restores access.", lang)}`);
      setNewEmail(""); setNewPhone("");
    } catch (e) { setChangeMsg(apiErr(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-8 fadeup" data-testid="account-page">
      <div>
        <h1 className="font-head text-4xl font-light">{tp("Account & Security", lang)}</h1>
        <p className="opacity-60">{tp("Manage your recovery email and update your contact details securely.", lang)}</p>
      </div>

      <Card className="space-y-3" data-testid="account-cs-profile">
        <div className="flex items-center gap-2 font-head"><IdCard size={18} className="text-[var(--primary)]" /> {tp("Customer Service Profile", lang)}</div>
        <p className="text-sm opacity-60">{tp("Keep this current so our team can support and verify you. Two phone numbers, a memorable word and a memorable phrase / public body are required; a postal address is optional.", lang)}</p>
        {acct && <ProfileForm initial={acct.profile} initialConsents={acct.consents} showConsents={false} onSaved={reloadAcct} />}
      </Card>

      <Card className="space-y-3" data-testid="account-recovery">
        <div className="flex items-center gap-2 font-head"><ShieldCheck size={18} className="text-[var(--primary)]" /> {tp("Recovery email", lang)}</div>
        <p className="text-sm opacity-60">{tp("Login email", lang)}: <b>{user?.email}</b>. {tp("Your recovery email is used to reset your password and to confirm any change to your email or phone.", lang)}</p>
        <Input label={tp("Recovery email", lang)} type="email" value={recEmail} onChange={(e) => setRecEmail(e.target.value)} data-testid="account-recovery-email" />
        {recMsg && <div className="text-sm" data-testid="account-recovery-msg">{recMsg}</div>}
        <div className="flex justify-end"><Button onClick={saveRecovery} disabled={busy || !recEmail.trim()} data-testid="account-recovery-save"><Mail size={15} /> {tp("Save recovery email", lang)}</Button></div>
      </Card>

      <Card className="space-y-3" data-testid="account-change">
        <div className="flex items-center gap-2 font-head"><Phone size={18} className="text-[var(--primary)]" /> {tp("Change email or phone", lang)}</div>
        <div className="flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle size={14} className="mt-0.5" /> {tp("For your protection, a confirmation is sent to BOTH your current phone and recovery email. The change applies only when you confirm. If you decline, your access is blocked until the vendor restores it.", lang)}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label={tp("New email (optional)", lang)} type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="account-new-email" />
          <Input label={tp("New phone (optional)", lang)} value={newPhone} onChange={(e) => setNewPhone(e.target.value)} data-testid="account-new-phone" />
        </div>
        {changeMsg && <div className="rounded-md border border-[var(--primary)]/40 px-3 py-2 text-sm" data-testid="account-change-msg">{changeMsg}</div>}
        <div className="flex justify-end"><Button onClick={requestChange} disabled={busy || (!newEmail.trim() && !newPhone.trim())} data-testid="account-change-request">{tp("Request change", lang)}</Button></div>
      </Card>

      <TrustedDevicesCard />

      <Card className="space-y-3" data-testid="account-security-log">
        <div className="flex items-center gap-2 font-head"><History size={18} className="text-[var(--primary)]" /> {tp("Security activity", lang)}</div>
        <p className="text-sm opacity-60">{tp("Recent sign-ins, password recoveries and contact-change events on your account. If you see something you didn't do, contact the vendor.", lang)}</p>
        <div className="max-h-64 overflow-y-auto rounded-md border border-black/10 dark:border-white/10" data-testid="account-events">
          {events.length === 0
            ? <div className="p-3 text-sm opacity-50">{tp("No activity yet.", lang)}</div>
            : events.map((e, i) => (
              <div key={i} className="flex items-center gap-3 border-b border-black/5 dark:border-white/5 px-3 py-2 text-sm last:border-0">
                {e.type === "contact_change_declined"
                  ? <Badge color="danger">{tp("alert", lang)}</Badge>
                  : e.type === "login" ? <Badge color="neutral">{tp("login", lang)}</Badge> : <Badge color="info">{tp("security", lang)}</Badge>}
                <div className="flex-1">{e.label}{e.detail ? <span className="opacity-50"> · {e.detail}</span> : ""}</div>
                <div className="text-xs opacity-50 whitespace-nowrap">{(e.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
            ))}
        </div>
      </Card>
      <MonthlyLogsCard />
    </div>
  );
}
